import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';

export interface CMSUrlPrefixes {
  article?: string;
  articles?: string;
  author?: string;
  authors?: string;
  tag?: string;
  tags?: string;
  category?: string;
  categories?: string;
}

export interface CMSSettings {
  id: string;
  brand_id: string;
  site_url: string | null;
  site_name: string | null;
  site_tagline: string | null;
  default_meta_title: string | null;
  default_meta_description: string | null;
  default_og_image: string | null;
  favicon_url: string | null;
  robots_txt_content: string | null;
  sitemap_enabled: boolean;
  sitemap_config: {
    include_articles?: boolean;
    include_courses?: boolean;
    include_academies?: boolean;
    include_job_posts?: boolean;
    changefreq?: string;
    priority?: number;
  };
  analytics_codes: {
    google_analytics_id?: string;
    gtm_id?: string;
    facebook_pixel_id?: string;
    linkedin_insight_tag?: string;
    hotjar_id?: string;
  };
  custom_head_scripts: string | null;
  custom_body_scripts: string | null;
  pwa_config: {
    theme_color?: string;
    background_color?: string;
    display?: string;
    start_url?: string;
  };
  url_prefixes: CMSUrlPrefixes | null;
  created_at: string;
  updated_at: string;
}

export type CMSSettingsInput = Omit<CMSSettings, 'id' | 'brand_id' | 'created_at' | 'updated_at'>;

const defaultSettings: CMSSettingsInput = {
  site_url: null,
  site_name: null,
  site_tagline: null,
  default_meta_title: null,
  default_meta_description: null,
  default_og_image: null,
  favicon_url: null,
  robots_txt_content: null,
  sitemap_enabled: true,
  sitemap_config: {
    include_articles: true,
    include_courses: true,
    include_academies: true,
    include_job_posts: true,
    changefreq: 'weekly',
    priority: 0.8,
  },
  analytics_codes: {},
  custom_head_scripts: null,
  custom_body_scripts: null,
  pwa_config: {
    theme_color: '#000000',
    background_color: '#ffffff',
    display: 'standalone',
    start_url: '/',
  },
  url_prefixes: {
    article: 'blog',
    articles: 'blog',
    author: 'author',
    authors: 'authors',
    tag: 'tag',
    tags: 'tags',
    category: 'category',
    categories: 'categories',
  },
};

export function useCMSSettings() {
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['cms-settings', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return null;

      const { data, error } = await supabase
        .from('os_cms_settings')
        .select('*')
        .eq('brand_id', currentBrandId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching CMS settings:', error);
        throw error;
      }

      // Return default settings if no data found, so we have a stable reference
      if (!data) {
        return { ...defaultSettings, brand_id: currentBrandId, id: '' } as CMSSettings;
      }

      return data as CMSSettings;
    },
    enabled: !!currentBrandId,
  });

  const saveMutation = useMutation({
    mutationFn: async (input: Partial<CMSSettingsInput>) => {
      if (!currentBrandId) throw new Error('No brand selected');

      const payload = {
        brand_id: currentBrandId,
        ...input,
        // Cast url_prefixes to Json type for Supabase
        url_prefixes: input.url_prefixes as Json,
        updated_at: new Date().toISOString(),
      };

      if (settings?.id) {
        // Update existing
        const { data, error } = await supabase
          .from('os_cms_settings')
          .update(payload)
          .eq('id', settings.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('os_cms_settings')
          .insert(payload)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cms-settings', currentBrandId] });
      toast.success('CMS settings saved successfully');
    },
    onError: (error) => {
      console.error('Error saving CMS settings:', error);
      toast.error('Failed to save CMS settings');
    },
  });

  const generateDefaultRobotsTxt = () => {
    return `# robots.txt
User-agent: *
Allow: /

# Disallow admin and private areas
Disallow: /admin/
Disallow: /api/
Disallow: /auth/
Disallow: /dashboard/
Disallow: /settings/
Disallow: /private/

# Crawl-delay for polite crawling
Crawl-delay: 1
`;
  };

  return {
    settings,
    isLoading,
    error,
    save: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    generateDefaultRobotsTxt,
    defaultSettings,
  };
}
